import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FileText, Plus, Search } from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import Loader from "../Loader";
import "../CRMUpdatedVersion/LeadsUpdatedVersion.css";
import "./InvoiceStyle.css";

function isQuoteRow(inv) {
  const t = String(inv?.document_type || "").toLowerCase();
  return t === "quote" || t === "proforma";
}

export default function Quotes() {
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const token = decryptToken("accessToken") || "";
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [convertingId, setConvertingId] = useState(null);

  const fetchQuotes = async () => {
    if (!isLoggedIn?.main_id) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${url}/newinvoice/all/${isLoggedIn.main_id}?document_type=quote`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Could not load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleConvert = async (quote) => {
    const result = await Swal.fire({
      title: "Convert to invoice?",
      html: `
        <p style="margin-bottom:12px">Quote <strong>${quote.invoice_number}</strong> will become a published invoice with a new invoice number.</p>
        <label style="display:block;margin:8px 0;text-align:left">
          <input id="swal-send-mail" type="checkbox" /> Email invoice to customer
        </label>
        <label style="display:block;text-align:left">
          <input id="swal-send-whatsapp" type="checkbox" /> Send WhatsApp to customer
        </label>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Convert",
      preConfirm: () => ({
        send_mail: document.getElementById("swal-send-mail")?.checked || false,
        send_whatsapp: document.getElementById("swal-send-whatsapp")?.checked || false,
      }),
    });
    if (!result.isConfirmed) return;

    setConvertingId(quote.id);
    try {
      const res = await fetch(`${url}/newinvoice/convert-to-invoice/${quote.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          publish: true,
          send_mail: result.value?.send_mail === true,
          send_whatsapp: result.value?.send_whatsapp === true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Conversion failed");
      toast.success(`Created invoice ${data.invoice?.invoice_number}`);
      await fetchQuotes();
      if (data.invoice?.id) {
        navigate(`/invoice-detail/${data.invoice.id}`);
      }
    } catch (err) {
      toast.error(err.message || "Could not convert quote");
    } finally {
      setConvertingId(null);
    }
  };

  const filtered = quotes.filter((q) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      String(q.invoice_number || "").toLowerCase().includes(s) ||
      String(q.customer_name || "").toLowerCase().includes(s)
    );
  });

  if (loading) return <Loader />;

  return (
    <div className="lsuv-oms">
      <div className="lsuv-oms__header">
        <div className="lsuv-tabs-oms">
          <div className="lsuv-tab-oms lsuv-tab-oms-active">
            <FileText size={16} style={{ marginRight: 6 }} />
            Quotes & proforma
          </div>
        </div>
        <div className="lsuv-header-icons-oms" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div
            className="text-black size-8 rounded-full flex justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer"
            onClick={() => setShowSearch((v) => !v)}
          >
            <Search size={18} />
          </div>
          <button type="button" className="omsnewbutton-copy" onClick={() => navigate("/new-quote")}>
            <Plus size={16} style={{ marginRight: 4 }} />
            New quote
          </button>
        </div>
      </div>

      {showSearch && (
        <div style={{ marginBottom: 12 }}>
          <input
            type="search"
            placeholder="Search quote or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", maxWidth: 320, padding: "8px 12px" }}
          />
        </div>
      )}

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Quote #</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Valid until</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                  No quotes yet. Create one to send estimates before invoicing.
                </td>
              </tr>
            ) : (
              filtered.map((q) => {
                const converted = Boolean(q.converted_to_id);
                const status = converted
                  ? "Converted"
                  : q.dpstatus || "Draft";
                return (
                  <tr key={q.id}>
                    <td>{q.invoice_number}</td>
                    <td>{q.customer_name}</td>
                    <td>{q.invoice_date}</td>
                    <td>{q.due_date}</td>
                    <td>₹{parseFloat(q.grand_total || 0).toFixed(2)}</td>
                    <td>{status}</td>
                    <td style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="omsnewbutton-copy"
                        onClick={() => navigate(`/invoice-detail/${q.id}`)}
                      >
                        View
                      </button>
                      {!converted && isQuoteRow(q) ? (
                        <button
                          type="button"
                          className="omsnewbutton-copy"
                          disabled={convertingId === q.id}
                          onClick={() => handleConvert(q)}
                        >
                          {convertingId === q.id ? "Converting…" : "Convert to invoice"}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
